import { useRef } from 'react';
import { Prompt, PromptProps, useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { Modal } from '@uniquegood/realworld-studio-design';
import { useModalState } from '@/hooks';

interface RouteModalProps {
  when: boolean | (() => boolean);
  isValid?: boolean;
  onSubmit(e?: unknown): void | boolean | Promise<void | boolean>;
  loading?: boolean;
}

export default function RouteModal({ when, isValid, onSubmit, loading }: RouteModalProps) {
  const { modal, openModal, closeModal } = useModalState();
  const nextPathname = useRef<string>();
  const allowNextTransition = useRef(false);
  const isEvaluatingTransition = useRef(false);
  const history = useHistory();

  const handleTransition: PromptProps['message'] = (location) => {
    if (allowNextTransition.current) {
      allowNextTransition.current = false;
      return true;
    }

    const isFunctionWhen = typeof when === 'function';
    const shouldBlock = isFunctionWhen ? when() : when;

    if (!shouldBlock) {
      if (!isFunctionWhen) {
        return true;
      }

      nextPathname.current = location.pathname;

      if (!isEvaluatingTransition.current) {
        isEvaluatingTransition.current = true;
        setTimeout(() => {
          isEvaluatingTransition.current = false;
          const shouldBlockAfterTick = when();

          if (shouldBlockAfterTick) {
            openRouteModal();
            return;
          }

          navigateToNextLocation();
        }, 0);
      }

      return false;
    }

    if (!modal.open) {
      openRouteModal();
      nextPathname.current = location.pathname;
      return false;
    }

    return true;
  };

  function navigateToNextLocation() {
    closeModal();
    if (nextPathname.current) {
      allowNextTransition.current = true;
      history.push(nextPathname.current);
    }
  }

  function openRouteModal() {
    const isFunctionWhen = typeof when === 'function';
    const shouldBlock = isFunctionWhen ? when() : when;

    if (!shouldBlock) {
      navigateToNextLocation();
      return;
    }
    openModal({
      title: '저장하지 않은 내용이 있어요',
      children: (
        <div>
          페이지를 벗어나면 변경사항이 사라져요. 변경한 사항을 저장하시겠어요?
          <br />
          <span css={AiDescription}>AI 만들기로 사용된</span>{' '}
          <span css={WarningText}>크레딧은 환불되지 않아요.</span>
        </div>
      ),
      primaryAction: {
        content: '저장',
        icon: 'save_solid',
        onAction: async () => {
          const isSaved = await onSubmit();

          if (isValid === false || isSaved === false) {
            closeModal();
            return;
          }

          navigateToNextLocation();
        }
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      },
      leftAction: {
        content: '저장 안함',
        onAction: navigateToNextLocation
      }
    });
  }

  return (
    <>
      <Prompt when message={handleTransition} />
      <Modal {...modal} primaryAction={{ ...modal.primaryAction, loading }} />
    </>
  );
}

const AiDescription = css`
  color: var(--text-Default, #191919);
  font-feature-settings: 'liga' off, 'clig' off;

  /* Body */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

const WarningText = css`
  color: var(--text-Critical, #d30000);
  font-feature-settings: 'liga' off, 'clig' off;

  /* Body */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;
